import { HARBOR_CRANES, ISLAND, ROUTES, SKYLINE_ZONES, WEATHER_BANDS, AMBIENT_PRESETS, pointAt } from '../data/world.js';

const TAU = Math.PI * 2;

function clamp(value, min, max) { return Math.max(min, Math.min(max, value)); }

export class WorldScene {
  constructor(canvas, { clock, getState }) {
    this.canvas = canvas;
    this.clock = clock;
    this.getState = getState;
    this.frame = 0;
    this.running = false;
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
  }

  start() {
    if (this.running) return;
    this.running = true;
    requestAnimationFrame((time) => this.draw(time));
  }

  destroy() {
    this.running = false;
    this.resizeObserver.disconnect();
  }

  resize() {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.floor(rect.width * dpr));
    const height = Math.max(1, Math.floor(rect.height * dpr));
    if (this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas.width = width;
      this.canvas.height = height;
    }
  }

  draw(time) {
    if (!this.running || !this.canvas.isConnected) {
      this.running = false;
      return;
    }
    this.resize();
    const rect = this.canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const ctx = this.canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    const snapshot = this.getState();
    const phase = this.clock.dayPhase();
    const preset = phase.night ? AMBIENT_PRESETS.night : phase.hours < 9 ? AMBIENT_PRESETS.dawn : phase.hours > 17 ? AMBIENT_PRESETS.dusk : AMBIENT_PRESETS.day;
    const cycle = this.clock.now() * 0.001;

    this.drawSky(ctx, rect.width, rect.height, preset, phase, cycle);
    this.drawWater(ctx, rect.width, rect.height, cycle);
    this.drawIsland(ctx, rect.width, rect.height, preset, phase);
    this.drawInfrastructure(ctx, rect.width, rect.height, cycle);
    this.drawSkyline(ctx, rect.width, rect.height, preset, phase, cycle);
    this.drawHarbor(ctx, rect.width, rect.height, preset, cycle);
    this.drawAmbientTraffic(ctx, rect.width, rect.height, cycle, snapshot);
    this.drawWeather(ctx, rect.width, rect.height, cycle, phase);
    this.drawSignals(ctx, rect.width, rect.height, cycle, snapshot);
    this.drawVignette(ctx, rect.width, rect.height, cycle, snapshot);
    this.frame += 1;
    requestAnimationFrame((next) => this.draw(next));
  }

  xy(width, height, point) { return [width * point[0] / 100, height * point[1] / 100]; }

  drawSky(ctx, width, height, preset, phase, cycle) {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, preset.sky[0]);
    gradient.addColorStop(0.62, preset.sky[1]);
    gradient.addColorStop(1, '#091722');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    const sunX = width * (0.16 + ((phase.hours / 24) * 0.72));
    const sunY = height * (0.22 + Math.abs(Math.sin((phase.hours / 24) * Math.PI)) * 0.16);
    const glow = ctx.createRadialGradient(sunX, sunY, 1, sunX, sunY, width * 0.28);
    glow.addColorStop(0, `${preset.glow}88`);
    glow.addColorStop(1, `${preset.glow}00`);
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, width, height * 0.75);
    if (phase.night) {
      ctx.fillStyle = 'rgba(204, 232, 226, .65)';
      for (let index = 0; index < 30; index += 1) {
        const x = ((index * 83) % 101) / 100 * width;
        const y = 12 + ((index * 47) % 40) / 100 * height;
        const twinkle = 0.5 + Math.sin(cycle * 1.5 + index) * 0.25;
        ctx.globalAlpha = twinkle;
        ctx.fillRect(x, y, 1.3, 1.3);
      }
      ctx.globalAlpha = 1;
    }
  }

  drawWater(ctx, width, height, cycle) {
    ctx.fillStyle = 'rgba(5, 25, 35, .5)';
    ctx.fillRect(0, height * 0.52, width, height * 0.48);
    ctx.strokeStyle = 'rgba(127, 205, 196, .12)';
    ctx.lineWidth = 1;
    for (let row = 0; row < 9; row += 1) {
      const y = height * (0.56 + row * 0.047);
      ctx.beginPath();
      for (let x = -20; x < width + 30; x += 22) {
        ctx.lineTo(x, y + Math.sin(cycle * 0.6 + x * 0.02 + row) * 2);
      }
      ctx.stroke();
    }
  }

  drawIsland(ctx, width, height, preset, phase) {
    ctx.beginPath();
    ISLAND.forEach((point, index) => {
      const [x, y] = this.xy(width, height, point);
      if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.closePath();
    ctx.shadowColor = 'rgba(0, 0, 0, .4)';
    ctx.shadowBlur = 20;
    ctx.fillStyle = preset.island;
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(145, 220, 196, .42)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.strokeStyle = 'rgba(206, 239, 207, .16)';
    ctx.beginPath();
    ctx.moveTo(width * .13, height * .48);
    ctx.bezierCurveTo(width * .3, height * .43, width * .59, height * .47, width * .86, height * .4);
    ctx.stroke();
    ctx.fillStyle = phase.night ? 'rgba(11, 35, 35, .2)' : 'rgba(137, 199, 126, .08)';
    ctx.beginPath();
    ctx.ellipse(width * .43, height * .5, width * .21, height * .16, -.2, 0, TAU);
    ctx.fill();
  }

  drawInfrastructure(ctx, width, height, cycle) {
    const drawRoute = (route, color, dash = []) => {
      ctx.beginPath();
      route.forEach((point, index) => {
        const [x, y] = this.xy(width, height, point);
        if (index === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.2;
      ctx.setLineDash(dash);
      ctx.lineDashOffset = -cycle * 8;
      ctx.stroke();
      ctx.setLineDash([]);
    };
    drawRoute(ROUTES.road, 'rgba(241, 212, 155, .27)', [5, 5]);
    drawRoute(ROUTES.belt, 'rgba(153, 212, 196, .22)', [2, 8]);
    drawRoute(ROUTES.rail, 'rgba(197, 185, 158, .22)', [1, 5]);
  }

  drawSkyline(ctx, width, height, preset, phase, cycle) {
    SKYLINE_ZONES.forEach((zone, zoneIndex) => {
      const baseY = height * (zone.y / 100);
      const zoneX = width * (zone.x / 100);
      const zoneWidth = width * (zone.width / 100);
      const count = zone.tone === 'industrial' ? 5 : 9;
      for (let index = 0; index < count; index += 1) {
        const buildingWidth = zoneWidth / (count + 1);
        const buildingHeight = height * ((zone.height / 100) * (0.35 + (((index * 13) % 17) / 25)));
        const x = zoneX + index * buildingWidth;
        const y = baseY - buildingHeight;
        ctx.fillStyle = zone.tone === 'industrial' ? 'rgba(25, 53, 57, .94)' : 'rgba(25, 45, 57, .94)';
        ctx.fillRect(x, y, buildingWidth * .78, buildingHeight);
        ctx.strokeStyle = 'rgba(129, 205, 190, .15)';
        ctx.strokeRect(x, y, buildingWidth * .78, buildingHeight);
        if (phase.night || zoneIndex % 2 === 0) {
          ctx.fillStyle = preset.glow + (phase.night ? '99' : '34');
          for (let window = 0; window < Math.floor(buildingHeight / 10); window += 1) {
            if ((window + index + Math.floor(cycle / 2)) % 4 === 0) ctx.fillRect(x + buildingWidth * .18, y + 7 + window * 10, 2, 3);
          }
        }
      }
    });
    ctx.fillStyle = 'rgba(122, 188, 171, .42)';
    ctx.fillRect(width * .51, height * .29, 2, height * .26);
    ctx.beginPath();
    ctx.moveTo(width * .51 - 5, height * .34);
    ctx.lineTo(width * .51 + 7, height * .34);
    ctx.strokeStyle = 'rgba(181, 227, 204, .4)';
    ctx.stroke();
  }

  drawHarbor(ctx, width, height, preset, cycle) {
    HARBOR_CRANES.forEach(([x, y], index) => {
      const px = width * x / 100;
      const py = height * y / 100;
      ctx.strokeStyle = 'rgba(247, 207, 138, .55)';
      ctx.lineWidth = 1.3;
      ctx.beginPath();
      ctx.moveTo(px, py); ctx.lineTo(px, py - 19); ctx.lineTo(px + 13, py - 19); ctx.lineTo(px + 13, py - 3);
      ctx.stroke();
      ctx.fillStyle = (Math.sin(cycle * 2 + index) > .1) ? preset.glow : 'rgba(255, 210, 130, .25)';
      ctx.fillRect(px + 5, py - 17, 3, 2);
    });
    ctx.fillStyle = 'rgba(197, 224, 206, .28)';
    ctx.fillRect(width * .39, height * .76, width * .24, 2);
  }

  drawAmbientTraffic(ctx, width, height, cycle, snapshot) {
    for (let index = 0; index < 9; index += 1) {
      const progress = (cycle * (0.008 + (index % 3) * 0.002) + index * .13) % 1;
      const [x, y] = this.xy(width, height, pointAt(ROUTES.road, progress));
      ctx.fillStyle = index % 3 === 0 ? 'rgba(255, 211, 132, .85)' : 'rgba(157, 224, 208, .72)';
      ctx.fillRect(x - 2, y - 1, 4, 2);
    }
    for (let index = 0; index < 4; index += 1) {
      const progress = (cycle * (0.003 + index * .0005) + index * .25) % 1;
      const [x, y] = this.xy(width, height, pointAt(ROUTES.maritime, progress));
      ctx.fillStyle = 'rgba(244, 229, 183, .8)';
      ctx.beginPath(); ctx.moveTo(x - 5, y); ctx.lineTo(x + 6, y); ctx.lineTo(x + 2, y + 3); ctx.lineTo(x - 4, y + 2); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = 'rgba(212, 241, 230, .18)';
      ctx.beginPath(); ctx.moveTo(x - 8, y + 4); ctx.lineTo(x + 8, y + 4); ctx.stroke();
    }
    const responseCount = Math.min(6, Object.values(snapshot.posture || {}).filter((value) => Number(value) > 0).length);
    for (let index = 0; index < responseCount; index += 1) {
      const progress = (cycle * .018 + index * .21) % 1;
      const [x, y] = this.xy(width, height, pointAt(ROUTES.belt, progress));
      ctx.fillStyle = '#76e0c9';
      ctx.fillRect(x - 3, y - 2, 6, 4);
      ctx.fillStyle = 'rgba(118, 224, 201, .22)';
      ctx.fillRect(x - 7, y - 1, 3, 2);
    }
  }

  drawWeather(ctx, width, height, cycle, phase) {
    WEATHER_BANDS.forEach((band, index) => {
      const drift = ((cycle * band.speed * 100) % 130) - 15;
      ctx.fillStyle = `rgba(202, 224, 222, ${band.opacity + (phase.night ? .04 : 0)})`;
      ctx.beginPath();
      ctx.ellipse(width * ((band.x + drift) / 100), height * (band.y / 100), width * band.width / 100, height * band.height / 100, index * .2, 0, TAU);
      ctx.fill();
    });
    if (Math.sin(cycle * .11) > .92) {
      ctx.fillStyle = 'rgba(111, 182, 200, .08)';
      ctx.fillRect(0, height * .1, width, height * .8);
      ctx.strokeStyle = 'rgba(161, 218, 219, .14)';
      for (let index = 0; index < 40; index += 1) {
        const x = (index * 47 + cycle * 23) % width;
        ctx.beginPath(); ctx.moveTo(x, height * .28); ctx.lineTo(x - 8, height * .55); ctx.stroke();
      }
    }
  }

  drawSignals(ctx, width, height, cycle, snapshot) {
    ctx.strokeStyle = 'rgba(119, 224, 207, .12)';
    ctx.setLineDash([2, 9]);
    ctx.beginPath();
    ctx.arc(width * .5, height * .51, Math.min(width, height) * (.21 + Math.sin(cycle * .3) * .01), 0, TAU);
    ctx.stroke();
    ctx.setLineDash([]);
    const activeSector = snapshot.activeEvent?.operation?.sector;
    if (activeSector) {
      const sectorPoint = snapshot.sectorCoords?.[activeSector];
      if (sectorPoint) {
        const [x, y] = this.xy(width, height, sectorPoint);
        const pulse = 16 + Math.sin(cycle * 5) * 4;
        ctx.strokeStyle = 'rgba(255, 200, 120, .65)';
        ctx.beginPath(); ctx.arc(x, y, pulse, 0, TAU); ctx.stroke();
        ctx.strokeStyle = 'rgba(255, 200, 120, .18)';
        ctx.beginPath(); ctx.arc(x, y, pulse + 10, 0, TAU); ctx.stroke();
      }
    }
  }

  drawVignette(ctx, width, height, cycle, snapshot) {
    const event = snapshot.activeEvent;
    const effect = snapshot.visualEffects?.until > Date.now() ? snapshot.visualEffects : null;
    if (!event && !effect) return;
    if (!event) {
      const duration = Math.max(1, effect.until - (effect.startedAt || effect.until - 7000));
      const fade = clamp((effect.until - Date.now()) / duration, 0, 1);
      ctx.fillStyle = `rgba(111, 224, 201, ${fade * .045})`;
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = `rgba(255, 202, 117, ${.45 + fade * .35})`;
      ctx.font = '10px DM Mono, monospace';
      ctx.fillText(`// ${effect.name?.toUpperCase() || effect.kind.toUpperCase()} / ${effect.outcome?.toUpperCase() || 'COMMAND RECEIVED'}`, 23, height - 25);
      if (effect.radio) {
        ctx.fillStyle = `rgba(184, 224, 211, ${fade * .65})`;
        ctx.fillText(effect.radio, 23, height - 10);
      }
      this.drawOperationEffect(ctx, width, height, cycle, effect, fade);
      return;
    }
    const strength = clamp((snapshot.eventWindowRemaining || 0) / 120000, 0, 1);
    ctx.fillStyle = `rgba(255, 184, 102, ${.025 + strength * .04})`;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(255, 202, 117, .8)';
    ctx.font = '10px DM Mono, monospace';
    ctx.fillText(`// ${event.operation.visual.toUpperCase()} / ${Math.ceil((snapshot.eventWindowRemaining || 0) / 1000)}s`, 23, height - 25);
    ctx.fillStyle = 'rgba(184, 224, 211, .7)';
    ctx.fillText(event.operation.radio, 23, height - 10);
    if (event.operation.visual === 'cable' && Math.sin(cycle * 6) > .72) {
      ctx.strokeStyle = 'rgba(255, 153, 137, .65)';
      ctx.beginPath(); ctx.moveTo(width * .3, height * .47); ctx.lineTo(width * .42, height * .52); ctx.lineTo(width * .57, height * .47); ctx.stroke();
    }
  }

  drawOperationEffect(ctx, width, height, cycle, effect, fade) {
    const color = effect.outcome === 'contained' ? '118, 224, 201' : '255, 202, 117';
    ctx.strokeStyle = `rgba(${color}, ${fade * .55})`;
    ctx.fillStyle = `rgba(${color}, ${fade * .72})`;
    ctx.lineWidth = 1.4;
    if (effect.kind === 'aircraft') {
      const progress = (cycle * .08) % 1;
      const x = width * (.12 + progress * .76);
      const y = height * (.29 + Math.sin(cycle * 2) * .015);
      ctx.beginPath(); ctx.moveTo(x - 13, y); ctx.lineTo(x + 8, y); ctx.lineTo(x + 13, y - 3); ctx.moveTo(x + 2, y); ctx.lineTo(x - 4, y + 5); ctx.stroke();
    } else if (effect.kind === 'convoy' || effect.kind === 'logistics') {
      for (let index = 0; index < 4; index += 1) {
        const progress = (cycle * .035 + index * .18) % 1;
        const x = width * (.12 + progress * .76);
        const y = height * (.7 - progress * .08);
        ctx.fillRect(x, y, 7, 3);
      }
    } else if (effect.kind === 'cable') {
      ctx.beginPath();
      ctx.arc(width * .42, height * .52, 22 + Math.sin(cycle * 5) * 5, 0, TAU);
      ctx.arc(width * .57, height * .47, 15 + Math.sin(cycle * 5 + 1) * 4, 0, TAU);
      ctx.stroke();
    } else {
      for (let index = 0; index < 3; index += 1) {
        ctx.beginPath();
        ctx.arc(width * .5, height * .55, 20 + index * 15 + ((cycle * 12) % 12), 0, TAU);
        ctx.stroke();
      }
    }
  }
}
